import { useState } from 'react';
import { useGo } from '@refinedev/core';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, Clock, ExternalLink } from 'lucide-react';
import { useExpiringSoon, type ExpiringSubscriptionItem } from '@/hooks/useDashboardAnalytics';
import { differenceInDays, format } from 'date-fns';
import { ru } from 'date-fns/locale';

interface ExpiringSubscriptionsWidgetProps {
  className?: string;
}

function getDisplayName(item: ExpiringSubscriptionItem): string {
  const u = item.user;
  if (!u) { return `Пользователь #${item.userId}`; }
  const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ');
  return fullName || u.username || u.email || `#${u.id}`;
}

function getInitials(item: ExpiringSubscriptionItem): string {
  const u = item.user;
  if (!u) { return '?'; }
  const first = u.firstName?.[0] ?? '';
  const last = u.lastName?.[0] ?? '';
  return (first + last).toUpperCase() || u.username?.[0]?.toUpperCase() || '?';
}

function getContact(item: ExpiringSubscriptionItem): string | null {
  const u = item.user;
  if (!u) { return null; }
  if (u.username) { return `@${u.username}`; }
  if (u.email) { return u.email; }
  if (u.phone) { return u.phone; }
  if (u.telegramId) { return `tg:${u.telegramId}`; }
  return null;
}

function getDaysLeft(endDate: string): number {
  return differenceInDays(new Date(endDate), new Date());
}

function getUrgencyVariant(daysLeft: number): 'destructive' | 'outline' | 'secondary' {
  if (daysLeft <= 1) { return 'destructive'; }
  if (daysLeft <= 3) { return 'outline'; }
  return 'secondary';
}

function getUrgencyLabel(daysLeft: number): string {
  if (daysLeft === 0) { return 'Сегодня'; }
  if (daysLeft === 1) { return 'Завтра'; }
  return `${daysLeft} дн.`;
}

const PERIOD_OPTIONS = [
  { label: '3 дня', value: 3 },
  { label: '7 дней', value: 7 },
  { label: '14 дней', value: 14 },
  { label: '30 дней', value: 30 },
];

export function ExpiringSubscriptionsWidget({ className }: ExpiringSubscriptionsWidgetProps) {
  const [days, setDays] = useState(7);
  const { data, isLoading } = useExpiringSoon(days);
  const go = useGo();

  const items = data?.data ?? [];

  function handleGoToUser(userId: number) {
    go({ to: { resource: 'users', action: 'show', id: userId } });
  }

  function handleGoToSubscription(subId: number) {
    go({ to: { resource: 'subscriptions', action: 'show', id: subId } });
  }

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Истекающие подписки
          </CardTitle>
          <Skeleton className="h-7 w-24" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={`exp-skel-${i}`} className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-3 w-20" />
                </div>
                <Skeleton className="h-5 w-14" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Истекающие подписки
          {items.length > 0 && (
            <Badge variant="destructive" className="ml-1 text-xs">
              {items.length}
            </Badge>
          )}
        </CardTitle>
        <Select
          value={String(days)}
          onValueChange={(v) => setDays(Number(v))}
        >
          <SelectTrigger className="h-7 w-[100px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={String(opt.value)} className="text-xs">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <AlertTriangle className="h-8 w-8 mb-2 opacity-30" />
            <p className="text-sm">Нет истекающих подписок</p>
            <p className="text-xs mt-1">в ближайшие {days} дн.</p>
          </div>
        ) : (
          <ScrollArea className="h-[300px] pr-1">
            <div className="space-y-2">
              {items.map((item) => {
                const daysLeft = getDaysLeft(item.endDate);
                const displayName = getDisplayName(item);
                const contact = getContact(item);
                const planCode = item.subscriptionPlan?.code ?? '—';

                return (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 rounded-lg border p-2 hover:bg-accent/50 transition-colors"
                  >
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarFallback className="text-xs">
                        {getInitials(item)}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-medium truncate">{displayName}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                          onClick={() => handleGoToUser(item.userId)}
                          title="Открыть пользователя"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {contact && <span className="truncate">{contact}</span>}
                        {contact && <span>·</span>}
                        <button
                          type="button"
                          className="cursor-pointer hover:underline bg-transparent border-0 p-0 text-xs text-muted-foreground"
                          onClick={() => handleGoToSubscription(item.id)}
                          title="Открыть подписку"
                        >
                          {planCode}
                        </button>
                        <span>·</span>
                        <span>{format(new Date(item.endDate), 'd MMM', { locale: ru })}</span>
                      </div>
                    </div>

                    <Badge
                      variant={getUrgencyVariant(daysLeft)}
                      className="shrink-0 text-xs"
                    >
                      {getUrgencyLabel(daysLeft)}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
